import { BaseClient } from '../base-client';
import type { SavedReport } from '../report-definitions/types';
import type {
  AddShareInput,
  CreateDashboardInput,
  Dashboard,
  ListDashboardsQuery,
  PatchDashboardInput,
  PromoteTileInput,
  ShareEntryInput,
} from './types';

type ListResponse = { data: Dashboard[]; meta: { total: number; limit: number; offset: number } };
type One = { data: Dashboard };

/**
 * Typed client for saved dashboards (`/api/dashboards/*`).
 *
 * Everything here moves DEFINITIONS. Nothing returns a result set: to draw a
 * dashboard you walk its tiles and run each one's spec through
 * `AnalyticsClient.query`, which re-scopes to whoever is asking. That is the
 * whole reason sharing is safe, and it is why there is no `run` method here — a
 * second way to fetch rows would be a second place for the scope to be
 * forgotten.
 *
 * `report.view` is needed for every call and `dashboard.edit` for every write.
 * There is deliberately no `dashboard.view`: the numbers come from the
 * analytics endpoint, which asks for `report.view`.
 *
 * Beyond those, the rules are per ROW and the server decides them: you may
 * edit, delete, share and transfer what you OWN; you may open and duplicate
 * what was shared with you. `Dashboard.owned` tells the two apart.
 */
export class DashboardsClient extends BaseClient {
  /**
   * The dashboards this caller may open.
   *
   * `scope` partitions rather than overlaps: `mine` is ownership alone,
   * `shared` excludes what you own, and `all` is both. So a dashboard you own
   * and also shared with everyone appears once.
   */
  list(query: ListDashboardsQuery = {}): Promise<ListResponse> {
    return this.request('/api/dashboards', { query: { ...query } });
  }

  get(id: string): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}`);
  }

  create(input: CreateDashboardInput): Promise<One> {
    return this.request('/api/dashboards', { method: 'POST', body: input });
  }

  /**
   * Edit the saved definition. OWNER ONLY, and it changes the dashboard for
   * everybody it is shared with, immediately — there are no per-recipient
   * copies.
   */
  patch(id: string, input: PatchDashboardInput): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /**
   * Owner only. A HARD delete: the shares go with it.
   *
   * The saved reports its tiles referenced are NOT deleted — a dashboard
   * borrows reports rather than containing them.
   */
  remove(id: string): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /**
   * Take a copy, owned by whoever asked.
   *
   * The one mutation a RECIPIENT may make — it is what keeps a shared
   * dashboard from being a dead end. Shares are not carried over. The tiles
   * still point at the ORIGINAL's reports, which is correct: each one resolves
   * under the copier's own reach, so a report they cannot see shows a tombstone
   * rather than its contents. Omit `name` and the server picks a free one.
   */
  duplicate(id: string, name?: string): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
      body: name ? { name } : {},
    });
  }

  /** Hand the dashboard to somebody else, shares intact. Owner only. */
  transfer(id: string, ownerUserId: string): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}/transfer`, {
      method: 'POST',
      body: { ownerUserId },
    });
  }

  /**
   * Save a report built on the dashboard as a custom report. Owner only, and
   * needs `report.edit`. Acts on the SAVED dashboard; the response carries both
   * the new report and the dashboard as it now stands.
   */
  promoteTile(
    id: string,
    tileId: string,
    input: PromoteTileInput,
  ): Promise<{ data: { report: SavedReport; dashboard: Dashboard } }> {
    return this.request(
      `/api/dashboards/${encodeURIComponent(id)}/tiles/${encodeURIComponent(tileId)}/promote`,
      { method: 'POST', body: input },
    );
  }

  /**
   * Replace the WHOLE share list. Owner only, and needs `dashboard.share`.
   *
   * A PUT of the entire list, not add/remove: send `[]` to revoke everything.
   * The server de-duplicates, so sending the same person twice tidies rather
   * than fails.
   */
  replaceShares(id: string, shares: readonly ShareEntryInput[]): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}/shares`, {
      method: 'PUT',
      body: { shares },
    });
  }

  /**
   * Add people. Insert-only: an entry already on the list is left as it is.
   * The owner, or a recipient who can share (`Dashboard.canShare`).
   */
  addShares(id: string, shares: readonly AddShareInput[]): Promise<One> {
    return this.request(`/api/dashboards/${encodeURIComponent(id)}/shares`, {
      method: 'POST',
      body: { shares },
    });
  }

  /** Grant or withdraw resharing on one share. Owner only. */
  updateShare(id: string, shareId: string, input: { canShare: boolean }): Promise<One> {
    return this.request(
      `/api/dashboards/${encodeURIComponent(id)}/shares/${encodeURIComponent(shareId)}`,
      { method: 'PATCH', body: input },
    );
  }

  /** Remove one share: the owner any, a resharer only those they added. */
  removeShare(id: string, shareId: string): Promise<One> {
    return this.request(
      `/api/dashboards/${encodeURIComponent(id)}/shares/${encodeURIComponent(shareId)}`,
      { method: 'DELETE' },
    );
  }
}
